import { TFunction } from 'react-i18next';
import { head, tail } from 'utility/snippets';
import DraggableStepCard from '__pages/AutomationEngine/Engine/Steps/DraggableStepCard';
import {
  fieldRequiresWizard,
  fieldToModifyfieldType,
} from 'pages/AutomationEngine/dialogs/ActionWizard/subsections/ModifyRelatedEntities/common';
import {
  mreChangeTypeOptionsConst,
  recordSourceOptionsConst,
} from 'pages/AutomationEngine/dialogs/ActionWizard/subsections/ModifyRelatedEntities/ts-common';
import { emptyLongDash } from 'components/Inputs/DateTimeInput/helpers';
import {
  getFieldValue,
  getLabelWithDeleted,
  getValueOrDeleted,
} from './action-helpers';
import { CardProps } from '../../types';
import {
  Automation,
  DeletedOption,
  Field,
  Step,
} from 'pages/AutomationEngine/types';
import { ModifyRelatedEntriesValues } from 'pages/AutomationEngine/dialogs/ActionWizard/subsections/ModifyRelatedEntities/types';

export const content = ({ step }: { step: Step }) => step.description;

export const getMultiValue = (
  value: any[],
  relatedObjectName: string,
  relatedEntityName: string,
  relationshipFieldNames: string,
  t: TFunction
) => {
  if (!Array.isArray(value) || value.length === 0) {
    return t(
      'Clear Related Entity ‘{{relatedObjectName}}: {{relatedEntityName}}’ Field',
      {
        relatedObjectName,
        relatedEntityName,
      }
    );
  }

  const options = value.map((val) => `‘${getLabelWithDeleted(val, t)}’`);

  if (value.length === 1) {
    return t(
      'Clear Related Entity ‘{{relatedObjectName}}: {{relatedEntityName}}’ Field {{option}} on ‘{{relationshipFieldNames}}’',
      {
        option: options[0],
        relatedObjectName,
        relatedEntityName,
        relationshipFieldNames,
      }
    );
  }

  if (value.length <= 3) {
    const lastOption = options.pop();
    return t(
      'Clear Related Entity ‘{{relatedObjectName}}: {{relatedEntityName}}’ Fields {{options}}, and {{lastOption}} on ‘{{relationshipFieldNames}}’',
      {
        options: options.join(', '),
        relatedObjectName,
        relatedEntityName,
        relationshipFieldNames,
        lastOption,
      }
    );
  }
  return t(
    'Clear Related Entity ‘{{relatedObjectName}}: {{relatedEntityName}}’ Fields {{options}}, and {{count}} more on ‘{{relationshipFieldNames}}’',
    {
      options: options.slice(0, 2).join(', '),
      relatedObjectName,
      relatedEntityName,
      relationshipFieldNames,
      count: options.length - 2,
    }
  );
};

const getDisplayValue = (relatedField: any, t: TFunction) => {
  const payload = {
    fieldToModify: relatedField?.fieldToModify?.fieldData,
    specificFieldValue: relatedField?.specificFieldValue,
  };

  return getFieldValue(payload, t);
};

const getEnteredValue = (enteredValue: any) =>
  enteredValue || enteredValue === 0 ? enteredValue : emptyLongDash; // treat 0 as not empty;

type GetDescription = {
  step: Step & {
    details: ModifyRelatedEntriesValues;
  };
  automation: Automation;
  fields: Array<Field>;

  t: TFunction;
};

export const getDescription = ({ step, automation, t }: GetDescription) => {
  const { details } = step;

  const {
    automationTargetRelationshipFields,
    fieldsToModify,
    objectToModify,
    recordSource,
    recordSourceVariables,
  } = details;
  const fieldsToModifyHead = head(fieldsToModify || []);
  const fieldsToModifyTail = tail(fieldsToModify || []);

  const relatedObjectName = getValueOrDeleted(objectToModify?.label, t);
  const relatedEntityName = getValueOrDeleted(
    objectToModify?.entityName ?? objectToModify?.data?.entityName,
    t
  );
  const relatedFieldName = getValueOrDeleted(
    fieldsToModifyHead?.fieldToModify?.label,
    t
  );

  let relationshipFieldNames = '';

  if (recordSource === recordSourceOptionsConst.RELATED_OBJECT_FIELD) {
    relationshipFieldNames = automationTargetRelationshipFields?.length
      ? `${automationTargetRelationshipFields
          .map(({ label }: { label: string }) => label)
          .join(', ')}`
      : t('[Deleted]');
  } else {
    relationshipFieldNames = recordSourceVariables?.length
      ? `${recordSourceVariables.map(({ label }: DeletedOption) => label).join(', ')}`
      : t('[Deleted]');
  }

  const enteredValue = getEnteredValue(getDisplayValue(fieldsToModifyHead, t));

  // context object unless ENTITY_VARIABLE_FIELD then it's the same related object
  const objectName =
    fieldsToModifyHead?.valueType?.value ===
    mreChangeTypeOptionsConst.ENTITY_VARIABLE_FIELD
      ? fieldsToModifyHead?.variable?.meta?.dataSubtype?.objectName
      : automation?.customObject?.objectName;

  const updateMode = fieldsToModifyHead?.updateMode;

  if (updateMode === 'clear_fields') {
    return getMultiValue(
      fieldsToModifyHead?.fieldsToClear,
      relatedObjectName,
      relatedEntityName,
      relationshipFieldNames,

      t
    );
  }

  const fieldName = getValueOrDeleted(
    fieldsToModifyHead?.contextEntityField?.label,
    t
  );
  const variableName = getValueOrDeleted(
    fieldsToModifyHead?.variable?.label,
    t
  );

  const requiresWizardType = fieldRequiresWizard(
    fieldToModifyfieldType(fieldsToModifyHead?.fieldToModify)
  );

  return `${t(
    'Change ‘{{relatedObjectName}}: {{relatedEntityName}}’ ‘{{relatedFieldName}}’ Field on ‘{{relationshipFieldNames}}’ to ',
    {
      relatedObjectName,
      relatedEntityName,
      relatedFieldName,
      relationshipFieldNames,
    }
  )}${
    fieldsToModifyHead?.valueType?.value ===
    mreChangeTypeOptionsConst.SPECIFIC_VALUE
      ? t('‘{{enteredValue}}’', {
          enteredValue,
        })
      : fieldsToModifyHead?.valueType?.value ===
          mreChangeTypeOptionsConst.BLANK_VALUE
        ? t('‘Blank Value’')
        : fieldsToModifyHead?.valueType?.value ===
            mreChangeTypeOptionsConst.VARIABLE
          ? `Variable ‘${variableName}’`
          : requiresWizardType
            ? `${t('Value(s) from ‘{{objectName}}: {{fieldName}}’ Field', {
                objectName,
                fieldName,
              })}`
            : `${t('Value of ‘{{objectName}}: {{fieldName}}’ Field', {
                objectName,
                fieldName,
              })}`
  }${
    fieldsToModifyTail.length > 0
      ? t(', and {{fieldCount}} Additional Field Updates', {
          fieldCount: fieldsToModifyTail.length,
        })
      : ''
  }`;
};

export default function ModifyRelatedEntitiesCard({
  step,
  ...others
}: CardProps) {
  return <DraggableStepCard {...others}>{content({ step })}</DraggableStepCard>;
}
